import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Copy, Inbox } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { siteUrl } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Section } from "@/components/settings/Section";

export function ShowcaseSettings() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/profile/showcase").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const url = `${siteUrl()}/c/${f.slug}`;
  const save = async (body = f) => {
    try { const r = await api.patch("/profile/showcase", body); setF(r.data); toast.success("Portfolio saved"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Section title="Portfolio page" desc="A public page that shows your signed-off work and collects new client inquiries." testId="showcase-settings">
      <div className="flex items-center justify-between border border-border rounded-lg p-3 mb-4">
        <div><div className="text-sm font-medium">Make my portfolio public</div><div className="text-xs text-muted-foreground break-all">{url}</div></div>
        <Switch data-testid="showcase-enabled-switch" checked={f.showcase_enabled} onCheckedChange={(v) => save({ showcase_enabled: v })} />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label>Link name</Label><Input data-testid="showcase-slug-input" value={f.slug || ""} onChange={set("slug")} /></div>
        <div><Label>Trade</Label><Input data-testid="showcase-trade-input" value={f.trade} onChange={set("trade")} placeholder="General contractor" /></div>
        <div><Label>Service area</Label><Input data-testid="showcase-area-input" value={f.service_area} onChange={set("service_area")} placeholder="Austin, TX" /></div>
      </div>
      <div className="mt-3"><Label>Short bio</Label><Textarea data-testid="showcase-bio-input" value={f.bio} onChange={set("bio")} className="min-h-[90px]" placeholder="What you do, who you do it for, and why clients pick you." /></div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button data-testid="showcase-save-btn" onClick={() => save({ slug: f.slug, trade: f.trade, service_area: f.service_area, bio: f.bio })} className="bg-indigo-600 hover:bg-indigo-500 text-white">Save portfolio</Button>
        <Button variant="outline" data-testid="showcase-copy-btn" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Portfolio link copied")).catch(() => window.prompt("Copy your portfolio link:", url))}><Copy className="h-4 w-4 mr-2" />Copy link</Button>
        {f.showcase_enabled && <a href={url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" data-testid="showcase-view-btn"><ExternalLink className="h-4 w-4 mr-2" />View</Button></a>}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Tip: turn on “Show in portfolio” on any project in the Workspace to feature it.</p>
    </Section>
  );
}

const LEAD_STATUSES = ["new", "contacted", "won", "lost"];

export function LeadsInbox() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };
  return (
    <Section title="Leads inbox" desc="Inquiries from your portfolio page. You also get each one by email." testId="leads-inbox">
      {leads.length === 0 ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Inbox className="h-4 w-4" />No inquiries yet — share your portfolio link to start collecting leads.</div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="border border-border rounded-lg p-4" data-testid={`lead-${l.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-medium">{l.name}</div>
                  <a href={`mailto:${l.email}`} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">{l.email}</a>
                  {l.phone && <span className="text-xs text-muted-foreground"> · {l.phone}</span>}
                </div>
                <div className="flex gap-1">
                  {LEAD_STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(l.id, s)} data-testid={`lead-${l.id}-status-${s}`}
                      className={`text-xs px-2 py-1 rounded capitalize transition-colors ${l.status === s ? "bg-indigo-600 text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <p className="text-sm mt-2 whitespace-pre-wrap">{l.message}</p>
              <div className="text-xs text-muted-foreground mt-2">{fmtDateTime(l.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
